import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttitudeBehaviorDto, DailyHabitDto, FundamentalDto } from '@/api/recognition';
import { getBehaviorName, getFundamentalColor, getFundamentalName, getHabitName } from '@/api/recognition';
import { DRAG_MIME } from './postitShared';

export type BankTab = 'fundamentales' | 'comportamientos' | 'habitos' | 'impacto';

const IMPACT_KEYS = ['impact1', 'impact2', 'impact3', 'impact4'] as const;
// sonarjs/no-duplicate-string — 'ri-used'/'ri-can-place' se repetían como literales en las 4
// categorías del banco; se centralizan aquí para que el className de cada chip quede en un solo
// lugar por estado (usado vs. disponible).
const USED_CLASS = 'ri-used';
const CAN_PLACE_CLASS = 'ri-can-place';

/** Clave i18n del hint de la pestaña Hábitos, o `null` si ya hay hábitos disponibles para listar.
 *  Se resuelve con `if`/`return` en vez de ternarios anidados (sonarjs/no-nested-conditional) y
 *  cubre, en orden, los tres motivos por los que la pestaña puede no tener nada que listar: falta
 *  el Fundamental, falta el comportamiento requerido, o el Fundamental elegido no tiene hábitos. */
/** Arma un chip como fuente de arrastre: el navegador solo permite iniciar drag desde elementos ya
 *  habilitados (`draggable`), y solo los disponibles (`!used`) tienen algo que ofrecer al soltarse. */
function startWordDrag(event: DragEvent<HTMLButtonElement>, mime: string, value: string): void {
  event.dataTransfer.setData(mime, value);
  event.dataTransfer.effectAllowed = 'copy';
}

function habitsHintKey(
  fundamentalId: string | null,
  behaviorRequired: boolean,
  behaviorId: string | null,
  habitsCount: number,
): string | null {
  if (!fundamentalId) return 'ri.pickFundFirst';
  if (behaviorRequired && !behaviorId) return 'ri.pickBehaviorFirst';
  if (habitsCount === 0) return 'ri.noHabitsForFund';
  return null;
}

export interface WordBankTabsProps {
  activeTab: BankTab;
  onTabChange: (tab: BankTab) => void;
  funds: FundamentalDto[];
  fundamentalId: string | null;
  /** Color del Fundamental elegido (catálogo real) — comportamientos y hábitos se pintan con este
   *  mismo color, ya que ambos pertenecen al Fundamental activo (REQ-104). */
  fundColor: string | null;
  onSelectFundamental: (id: string) => void;
  behaviorRequired: boolean;
  behaviors: AttitudeBehaviorDto[];
  behaviorId: string | null;
  onSelectBehavior: (id: string) => void;
  habits: DailyHabitDto[];
  habitId: string | null;
  onSelectHabit: (id: string) => void;
  impactIdx: number | null;
  onSelectImpact: (idx: number) => void;
  lang: string;
}

/** Banco de palabras del post-it interactivo, organizado en pestañas por categoría (REQ-104). */
export function WordBankTabs({
  activeTab, onTabChange, funds, fundamentalId, fundColor, onSelectFundamental,
  behaviorRequired, behaviors, behaviorId, onSelectBehavior,
  habits, habitId, onSelectHabit, impactIdx, onSelectImpact, lang,
}: Readonly<WordBankTabsProps>) {
  const { t } = useTranslation('recognition');
  const habitsHint = habitsHintKey(fundamentalId, behaviorRequired, behaviorId, habits.length);

  const tabs: { id: BankTab; label: string }[] = [
    { id: 'fundamentales', label: t('ri.catAccion') },
    ...(behaviorRequired ? [{ id: 'comportamientos' as BankTab, label: t('ri.catComportamiento') }] : []),
    { id: 'habitos', label: t('ri.catHabito') },
    { id: 'impacto', label: t('ri.catImpacto') },
  ];

  return (
    <div className="ri-bank">
      {/* Texto siempre visible (no un tooltip por hover) — antes solo `clickHint` explicaba dónde
          elegir las palabras, pero como `title` nativo nunca aparece en touch y muchos usuarios de
          escritorio tampoco esperan a que salga: el banco quedaba sin ninguna pista permanente de
          que la selección ocurre aquí abajo, no en los espacios vacíos del post-it de arriba. */}
      <p className="ri-bank-caption">{t('ri.bankHint')}</p>
      <div className="ri-bank-tabs" role="tablist" aria-label={t('ri.bankTitle')}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`ri-bank-tab ${activeTab === tab.id ? 'ri-bank-tab--on' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="ri-bank-panel" role="tabpanel">
        {activeTab === 'fundamentales' && (
          <div className="ri-word-cloud">
            {funds.map((fund, index) => {
              const used = fundamentalId === fund.id;
              const color = getFundamentalColor(fund, index);
              return (
                <button
                  key={fund.id}
                  type="button"
                  className={`ri-word ${used ? USED_CLASS : CAN_PLACE_CLASS}`}
                  style={{ background: `${color}18`, borderColor: color, color }}
                  title={t('ri.tooltipFundamental')}
                  disabled={used}
                  draggable={!used}
                  onDragStart={(event) => startWordDrag(event, DRAG_MIME.fundamental, fund.id)}
                  onClick={() => onSelectFundamental(fund.id)}
                >
                  {getFundamentalName(fund, lang)}
                </button>
              );
            })}
          </div>
        )}

        {activeTab === 'comportamientos' && behaviorRequired && (
          !fundamentalId ? (
            <p className="ri-bank-hint">{t('ri.pickFundFirst')}</p>
          ) : (
            <div className="ri-word-cloud">
              {behaviors.map((behavior) => {
                const used = behaviorId === behavior.id;
                return (
                  <button
                    key={behavior.id}
                    type="button"
                    className={`ri-word ri-word--comportamiento ${used ? USED_CLASS : CAN_PLACE_CLASS}`}
                    style={fundColor ? { background: `${fundColor}18`, borderColor: fundColor, color: fundColor } : undefined}
                    title={t('ri.tooltipComportamiento')}
                    disabled={used}
                    draggable={!used}
                    onDragStart={(event) => startWordDrag(event, DRAG_MIME.behavior, behavior.id)}
                    onClick={() => onSelectBehavior(behavior.id)}
                  >
                    {getBehaviorName(behavior, lang)}
                  </button>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'habitos' && (
          habitsHint ? (
            <p className="ri-bank-hint">{t(habitsHint)}</p>
          ) : (
            <div className="ri-word-cloud">
              {habits.map((habit) => {
                const used = habitId === habit.id;
                return (
                  <button
                    key={habit.id}
                    type="button"
                    className={`ri-word ri-word--habito ${used ? USED_CLASS : CAN_PLACE_CLASS}`}
                    style={fundColor ? { background: `${fundColor}18`, borderColor: fundColor, color: fundColor } : undefined}
                    title={t('ri.tooltipHabito')}
                    disabled={used}
                    draggable={!used}
                    onDragStart={(event) => startWordDrag(event, DRAG_MIME.habit, habit.id)}
                    onClick={() => onSelectHabit(habit.id)}
                  >
                    {getHabitName(habit, lang)}
                  </button>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'impacto' && (
          <div className="ri-word-cloud">
            {IMPACT_KEYS.map((key, index) => {
              const used = impactIdx === index;
              return (
                <button
                  key={key}
                  type="button"
                  className={`ri-word ri-word--impacto-${index} ${used ? USED_CLASS : CAN_PLACE_CLASS}`}
                  title={t('ri.tooltipImpacto')}
                  disabled={used}
                  draggable={!used}
                  onDragStart={(event) => startWordDrag(event, DRAG_MIME.impact, String(index))}
                  onClick={() => onSelectImpact(index)}
                >
                  {t(`ri.${key}`)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default WordBankTabs;
